import { memo, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import { usePlaceImageName } from 'tripPlanner/hooks/Google/usePlaceImageName'
import type { ExploreItem } from 'tripPlanner/types/common'

const CARD_IMAGE_SIZE = 120
const CARD_MIN_HEIGHT = 160

const CardContainer = styled.div`
  display: flex;
  flex-direction: row;
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: border-color 0.15s ease;
  /*
    A floor, not a ceiling. Every card wants to be the same 160px, but the content
    is not bounded: a LuxPlus+ price stack (member price + "with LUX PLUS+" + the
    non-member row) is ~49px taller than a plain price, and a 2-line title on top of
    it needs more than 160px leaves. A hard height cropped whatever lost the fight -
    half a description line, or the bottom of the rating badge - so let the rare tall
    card grow instead.
  */
  min-height: ${rem(CARD_MIN_HEIGHT)};

  &:hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

const ImageSection = styled.div`
  position: relative;
  width: ${rem(CARD_IMAGE_SIZE)};
  min-width: ${rem(CARD_IMAGE_SIZE)};
  min-height: ${rem(CARD_IMAGE_SIZE)};
  flex-shrink: 0;
`

/*
  Takes the image out of flow so its intrinsic aspect ratio can't drive the card
  height - it just fills the image column and object-fit crops it. Google Places
  photos come back at whatever aspect ratio the uploader shot them at, and a tall
  one used to stretch the whole card with it.
*/
const ImageFill = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
`

const PlaceImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  display: flex;
  align-items: center;
  justify-content: center;
`

const ContentSection = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${rem(8)} ${rem(12)};
  gap: ${rem(8)};
`

interface Props {
  place: ExploreItem
  onViewPlace: (place: ExploreItem) => void
  onHover?: (placeId: string | undefined) => void
  isHighlighted?: boolean
}

function ChatMapPlaceCard({
  place,
  onViewPlace,
  onHover = noop,
  isHighlighted,
}: Props) {
  const intl = useIntl()
  const [inViewRef, inView] = useInView({ triggerOnce: true })

  const photoName = place.photo?.name
  const { data: placeImage } = usePlaceImageName({
    name: photoName ?? '',
    options: { enabled: inView && Boolean(photoName) },
  })
  const placeImageUrl = placeImage?.url

  const viewLabel = intl.formatMessage({
    id: 'lpdk0rEqGFNdxaPIHTgS',
    defaultMessage: 'View',
    description: 'Button label to view place details from the map results drawer',
  })

  const handleMouseEnter = useCallback(() => {
    onHover(place.id)
  }, [onHover, place.id])

  const handleMouseLeave = useCallback(() => {
    onHover(undefined)
  }, [onHover])

  const handleViewClick = useCallback(() => {
    onViewPlace(place)
  }, [onViewPlace, place])

  const description = place.editorialSummary ?? place.primaryTypeDisplayName

  return (
    <CardContainer
      ref={inViewRef}
      data-testid="chat-map-place-card"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ImageSection>
        <ImageFill>
          {placeImageUrl && (
            <PlaceImage image={{ url: placeImageUrl }} alt={place.name} loading="lazy" />
          )}
          {!placeImageUrl && (
            <ImagePlaceholder>
              <LineMapMarkerIcon size="M" colour="light-foreground-alt" />
            </ImagePlaceholder>
          )}
        </ImageFill>
      </ImageSection>

      <ContentSection>
        <Group direction="vertical" gap={4}>
          <Heading variant="heading6" lineClamp={2}>{place.name}</Heading>
          {place.rating !== undefined && (
            <GooglePlaceRatingDisplay
              rating={place.rating}
              ratingsCount={place.userRatingCount}
              size="S"
            />
          )}
          {description && (
            <BodyText variant="small" colour="light-foreground-alt" lineClamp={2}>
              {description}
            </BodyText>
          )}
        </Group>

        <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="end" shrink={false}>
          <TextButton
            kind={isHighlighted ? 'primary' : 'secondary'}
            size="small"
            onClick={handleViewClick}
          >
            {viewLabel}
          </TextButton>
        </Group>
      </ContentSection>
    </CardContainer>
  )
}

export default memo(ChatMapPlaceCard)
